import React, { useRef } from 'react'
import ReactModal from 'react-modal';
import { ReactComponent as CloseIcon } from 'assets/img/x.svg'
import {
    Editor,
    EditorState,
    DraftHandleValue,
    Modifier,
    SelectionState,
    CompositeDecorator,
    convertFromRaw,
    convertToRaw,
} from 'draft-js';
import styles from '../KitFieldsType/KitFieldsType.module.scss'
import ContentDataSelector from 'containers/Workflows/ContentDataSelector/ContentDataSelector';
import { WorkflowCreationContext } from '../WorkflowCreation2';

// Mention component with remove functionality
const MentionComponent = ({ children, entityKey, contentState, blockKey, start, end, setEditorState, editorState }: any) => {
    const { workflowData } = React.useContext(WorkflowCreationContext);

    const removeMention = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Create selection for the mention
        const selection = new SelectionState({
            anchorKey: blockKey,
            anchorOffset: start,
            focusKey: blockKey,
            focusOffset: end,
            isBackward: false,
            hasFocus: true
        });

        // Remove the mention text
        const newContentState = Modifier.removeRange(contentState, selection, 'forward');

        // Create new editor state
        const newEditorState = EditorState.push(
            editorState,
            newContentState,
            'remove-range'
        );

        // Set focus after the removed mention
        const newSelection = new SelectionState({
            anchorKey: blockKey,
            anchorOffset: start,
            focusKey: blockKey,
            focusOffset: start,
            isBackward: false,
            hasFocus: true
        });

        const finalEditorState = EditorState.forceSelection(newEditorState, newSelection);
        setEditorState(finalEditorState);
    };

    return (
        <span
            className={styles.pill}
            contentEditable={false}
        >
            <span>{children}</span>
            {
                workflowData && workflowData.status === 'active'
                    ?
                    <></>
                    :
                    <span
                        onClick={removeMention}
                        className={styles.pillClose}
                        title="Remove mention"
                    >
                        <CloseIcon />
                    </span>
            }
        </span>
    );
};

// Decorator strategy to find mentions
const findMentionEntities = (contentBlock: any, callback: any, contentState: any) => {
    contentBlock.findEntityRanges(
        (character: any) => {
            const entityKey = character.getEntity();
            return (
                entityKey !== null &&
                contentState.getEntity(entityKey).getType() === 'MENTION'
            );
        },
        callback
    );
};

// Helper function to create editor state from saved data
const createEditorStateFromData = (editorContent: any, decorator: CompositeDecorator) => {
    if (editorContent && editorContent.rawContentState) {
        try {
            const contentState = convertFromRaw(editorContent.rawContentState);
            return EditorState.createWithContent(contentState, decorator);
        } catch (error) {
            console.warn('Failed to restore editor state from saved data:', error);
        }
    }
    return EditorState.createEmpty(decorator);
};

const KitMappingFieldEditor = ({
    isOpen,
    closeModal,
    setIsModalOpen,
    filteredData,
    searchString,
    setSearchString,
    kitInputsType,
    handleSelectName,
    onFocusInput,
    editorContent,
    setEditorContent,
    workflowEvent,
}: any) => {
    const editorRef = useRef<Editor | null>(null);
    const { shouldShowDangerIcon, workflowData } = React.useContext(WorkflowCreationContext);
    const [atSymbolPosition, setAtSymbolPosition] = React.useState<number | null>(null);

    // Create decorator for mentions
    const decorator = React.useMemo(() => new CompositeDecorator([
        {
            strategy: findMentionEntities,
            component: (props: any) => (
                <MentionComponent
                    {...props}
                    setEditorState={setEditorState}
                    editorState={editorState}
                />
            ),
        },
    ]), []);

    // Initialize editor state from existing data or create empty
    const [editorState, setEditorState] = React.useState<EditorState>(() =>
        createEditorStateFromData(editorContent, decorator)
    );

    // Update editor state when editorContent changes (for external updates)
    React.useEffect(() => {
        if (editorContent && editorContent.rawContentState) {
            const currentRaw = convertToRaw(editorState.getCurrentContent());

            // Only update editorState if content changed
            if (JSON.stringify(currentRaw) !== JSON.stringify(editorContent.rawContentState)) {
                const newEditorState = createEditorStateFromData(editorContent, decorator);
                setEditorState(newEditorState);
            }
        }
    }, [editorContent.rawContentState, decorator]);


    const handleBeforeInput = (chars: string): DraftHandleValue => {
        if (chars === '@') {
            const selection = editorState.getSelection();
            setAtSymbolPosition(selection.getStartOffset());
            setIsModalOpen(true);
            onFocusInput();
            return 'handled'; // Dont Allow the @ to be inserted
        }
        return 'not-handled';
    };

    const insertMention = (selectedData: any) => {
        const currentContent = editorState.getCurrentContent();
        const selection = editorState.getSelection();

        // Create entity for the mention
        const contentStateWithEntity = currentContent.createEntity(
            'MENTION',
            'IMMUTABLE',
            {
                label: selectedData.label,
                value: selectedData.value,
                key: selectedData.key
            }
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        // Find the @ symbol position and replace it with the mention
        const blockKey = selection.getStartKey();
        const block = currentContent.getBlockForKey(blockKey);
        const blockText = block.getText();

        // Find the last @ symbol before current cursor position
        const cursorPosition = selection.getStartOffset();
        let atPosition = atSymbolPosition;

        if (atPosition === null) {
            // Fallback: find the last @ in the text
            atPosition = blockText.lastIndexOf('@', cursorPosition - 1);
        }

        if (atPosition !== -1) {
            // Create selection from @ symbol to current position
            const targetSelection = new SelectionState({
                anchorKey: blockKey,
                anchorOffset: atPosition,
                focusKey: blockKey,
                focusOffset: cursorPosition,
                isBackward: false,
                hasFocus: true
            });

            // Replace the @ symbol and any typed text with the mention
            const newContentState = Modifier.replaceText(
                contentStateWithEntity,
                targetSelection,
                `${selectedData.label}`,
                undefined,
                entityKey
            );

            // Create new editor state with decorator
            const newEditorState = EditorState.push(
                editorState,
                newContentState,
                'insert-characters'
            );

            // Move cursor to end of inserted mention
            const newSelection = newContentState.getSelectionAfter();
            const finalEditorState = EditorState.forceSelection(newEditorState, newSelection);

            setEditorState(finalEditorState);
        }

        // Reset at symbol position
        setAtSymbolPosition(null);
    };

    const handleEditorChange = (state: EditorState) => {
        setEditorState(state);
    };

    const handleDataSelected = (value: any, data: any) => {
        setSearchString('');
        insertMention(data);
        handleSelectName(data);
        closeModal();
    };

    React.useEffect(() => {
        const contentState = editorState.getCurrentContent();
        const plainText = contentState.getPlainText();

        // Extract mentions and build structured data
        const blocks = contentState.getBlocksAsArray();
        const mentions: any[] = [];

        // Build mentions array with position information
        blocks.forEach((block) => {
            block.findEntityRanges(
                (character) => {
                    const entityKey = character.getEntity();
                    return entityKey !== null &&
                        contentState.getEntity(entityKey).getType() === 'MENTION';
                },
                (start, end) => {
                    const entityKey = block.getEntityAt(start);
                    const entity = contentState.getEntity(entityKey);
                    const entityData = entity.getData();
                    mentions.push({
                        text: block.getText().slice(start, end),
                        ...entityData,
                        start,
                        end
                    });
                }
            );
        });

        // Create internal_key by replacing mentions with {{key}} format
        let internalKey = plainText;

        // Sort mentions by start position in descending order to replace from end to beginning
        // This prevents position shifts when replacing text
        const sortedMentions = [...mentions].sort((a, b) => b.start - a.start);

        sortedMentions.forEach((mention) => {
            const replacement = `{{${mention.key}}}`; // e.g., "{{first_name}}"

            // Replace the mention text with the key format
            const beforeMention = internalKey.substring(0, mention.start);
            const afterMention = internalKey.substring(mention.end);
            internalKey = beforeMention + replacement + afterMention;
        });

        // Save the raw content state for persistence
        const rawContentState = convertToRaw(contentState);

        setEditorContent((prev: any) => ({
            ...prev,
            internal_key: internalKey,
            text: plainText,
            mentions: mentions,
            rawContentState: rawContentState, // Add this for persistence
        }));
    }, [editorState]);

    return (
        <>
            <div
                style={{
                    border: '1px solid #d5d8df',
                    padding: '8px',
                    minHeight: '42px',
                    cursor: 'text',
                    borderRadius: '0.5rem',
                }}
                onClick={() => editorRef.current && editorRef.current.focus()}
            >
                <Editor
                    ref={editorRef}
                    editorState={editorState}
                    onChange={handleEditorChange}
                    handleBeforeInput={handleBeforeInput}
                    placeholder={`Type "@" here to add data for ${kitInputsType.label}`}
                    spellCheck={false}
                    readOnly={workflowData && workflowData.status === 'active'}
                />
            </div>

            <ReactModal
                isOpen={isOpen}
                onRequestClose={closeModal}
                contentLabel="Mention Someone"
                className={styles.AtMentionInput__modalContainer}
                style={{
                    // content: {
                    //     width: "500px",
                    //     maxWidth: "calc(100vw - 10px)",
                    //     minHeight: "auto",
                    //     borderRadius: "4px",
                    //     top: "50%",
                    //     left: "65%",
                    //     right: "auto",
                    //     bottom: "auto",
                    //     transform: "translate(-50%, -50%)",
                    //     position: "fixed",
                    //     background: "white",
                    //     border: 0,
                    //     padding: 0,
                    // },
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 99999,
                    }
                }}
            >
                <div className={styles.AtMentionInput__modal}>
                    <div className={styles.AtMentionInput__modal__header}>
                        <h1 className={styles.AtMentionInput__modal__header__h1}>
                            Insert data for {kitInputsType.label}
                        </h1>
                        <div className={styles.AtMentionInput__modal__closeIcon} onClick={closeModal}>
                            <CloseIcon />
                        </div>
                    </div>

                    <input
                        value={searchString}
                        onChange={(e: any) => setSearchString(e.target.value)}
                        className={styles.AtMentionInput__input}
                        placeholder='Search or select a field'
                        autoFocus
                    />

                    {
                        shouldShowDangerIcon() === false && workflowEvent && workflowEvent.event_resource_details &&
                        <span className={styles.InsertDataHeader}>
                            {/* {workflowEvent.resource_name.replace('-', ' ')}
                            {
                                workflowEvent.event_resource_details.event_resource_title
                                    ?
                                    `: ${workflowEvent.event_resource_details.event_resource_title}`
                                    :
                                    ''
                            } */}
                            Fields
                        </span>
                    }
                    <div className={styles.ActiontypesDropdown}>
                        <ContentDataSelector
                            data={filteredData && filteredData}
                            onDataSelected={handleDataSelected}
                            searchString={searchString}
                            labelStyle={styles.workflowActionlabelStyle}
                            containerClassName={styles.ActiontypesDropdown__Dropdown}
                            elementContainerClass={styles.workflowActionEl}
                        />
                    </div>
                </div>
            </ReactModal>
        </>
    );
};

export default KitMappingFieldEditor;